import type { TitleFilter } from '@/@types/graphql-types';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { MaybeRefOrGetter } from '@vueuse/core';

import type {
	GetSingleQtvSimilarTitlesQuery,
	GetSingleQtvSimilarTitlesQueryVariables,
} from '@/features/buybox/graphql/queries/GetSingleQTV.query';

import { GetSingleQtvSimilarTitlesDocument } from '@/features/buybox/graphql/queries/GetSingleQTV.query';

import { captureMessageForSentry } from '@/helpers/sentry-helper';

import { SimilarTitlesSorting } from '@/@types/graphql-types';
import { useLanguageStore } from '@/helpers/composables/useStores';
import { useQuery } from '@/helpers/composables/useApollo';

import { toValue } from '@vueuse/core';
import { computed } from 'vue';
import { useBingeAuPromo } from '@/helpers/composables/useBingeAUPromo';

const singleQTVBannerAndDisclaimer: Record<string, Record<string, { bannerText: string; disclaimerText: string }>> = {
	TW: {
		itu: {
			bannerText: '春節優惠: Apple TV 好戲 NT$15 起租，立即觀看',
			disclaimerText: '優惠期間 2 月 9 日至 22 日。僅限指定片單。新片 NT$190 起。新用戶可享免費試用一個月。',
		},
	},
	HK: {
		itu: {
			bannerText: '新春優惠: Apple TV 猛片 HK$8 起租，即刻睇戲',
			disclaimerText: '優惠期間 2 月 9 日至 22 日。僅限指定片單。新片 HK$58 起。新用戶可享免費試用一個月。',
		},
	},
};

export function useSingleQTV(
	title: MaybeRefOrGetter<TitleDetail>,
	providerShortName: MaybeRefOrGetter<string | null>,
	enabled: MaybeRefOrGetter<boolean>,
	experimentFilters?: MaybeRefOrGetter<TitleFilter | null>
) {
	const { country, language } = useLanguageStore();
	const _title = toValue(title);

	const { bannerAndDisclaimer: bingeAuPromoBannerAndDisclaimer } = useBingeAuPromo({ title });

	const shortName = computed(() => toValue(providerShortName) || '');
	const variables = computed<GetSingleQtvSimilarTitlesQueryVariables>(() => ({
		country: country.value,
		language: language.value,
		first: 1,
		titleId: _title.__typename === 'Season' ? _title.show.id : _title.id,
		filters: {
			packages: [shortName.value],
			...(toValue(experimentFilters) ?? {}),
		},
		offerFilters: {
			packages: [shortName.value],
			bestOnly: true,
		},
		sorting: SimilarTitlesSorting['ImdbScore'],
	}));

	const enableQuery = computed(() => toValue(enabled) && toValue(providerShortName) !== null);

	const { result, loading, onError } = useQuery<
		GetSingleQtvSimilarTitlesQuery,
		GetSingleQtvSimilarTitlesQueryVariables
	>(GetSingleQtvSimilarTitlesDocument, variables, { enabled: enableQuery });

	onError(error => {
		captureMessageForSentry('[Graphql GetSingleQtvSimilarTitlesQuery]', {
			where: '[SingleQTV.vue]',
			error,
		});
	});

	const sqtvBannerAndDisclaimer = computed(() => {
		if (bingeAuPromoBannerAndDisclaimer.value) return bingeAuPromoBannerAndDisclaimer.value;

		return singleQTVBannerAndDisclaimer[country.value]?.[shortName.value] ?? null;
	});

	const similarTitle = computed(() => {
		return result.value?.node?.__typename === 'Movie' || result.value?.node?.__typename === 'Show'
			? result?.value?.node?.similarTitlesV2.edges?.[0]
			: null;
	});

	const isSQTVEmpty = computed(() => {
		if (loading.value) return false;

		return similarTitle.value == null;
	});

	return {
		similarTitle,
		loading,
		isSQTVEmpty,
		sqtvBannerAndDisclaimer,
	};
}
